import { Link } from 'react-router';

import { Heading } from '../../atoms/Heading';
import { Image } from '../../atoms/Image';
import { Stack } from '../../atoms/Stack';
import { Text } from '../../atoms/Text';
import type { Article } from '../../../data/articles';
import { formatReadingTime } from '../../../utils/readingTime';
import type { TestIdProps } from '../../testing';
import styles from './ArticleCard.module.css';

export interface ArticleCardProps extends TestIdProps {
  article: Article;
  /** This card's image is the page's LCP candidate: load it eagerly with high priority. */
  priority?: boolean | undefined;
  className?: string | undefined;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatPublishedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return dateFormatter.format(date);
}

export function ArticleCard({
  article,
  priority = false,
  className,
  'data-testid': testId,
}: ArticleCardProps) {
  const classes = [styles['root'], className].filter(Boolean).join(' ');
  const dateLabel = formatPublishedAt(article.metadata.publishedAt);
  const metaLabel = `${dateLabel} · ${formatReadingTime(article.body)}`;

  return (
    <Link
      to={`/articles/${article.slug}`}
      className={classes}
      data-testid={testId ?? `article-card-${article.slug}`}
    >
      <Stack direction="column" gap="md" align="stretch">
        <div className={styles['imageWrap']}>
          <Image
            src={article.mainImage.src}
            alt=""
            width={article.mainImage.width}
            height={article.mainImage.height}
            sizes="(max-width: 768px) 90vw, 33vw"
            priority={priority}
            className={styles['image']}
          />
        </div>
        <Stack direction="column" gap="xs">
          <Text variant="label" tone="muted">
            {metaLabel}
          </Text>
          <Heading level={3} variant="editorial">
            {article.headline}
          </Heading>
          <Text variant="editorial" tone="muted">
            {article.excerpt}
          </Text>
        </Stack>
      </Stack>
    </Link>
  );
}
